import { useEffect, useRef, type ReactNode } from 'react'

export interface ModalProps {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
}

/**
 * Built on <dialog>, so focus trapping, Escape-to-close and the backdrop come
 * from the platform rather than from hand-rolled key handling that would
 * inevitably be subtly wrong.
 */
export function Modal({ open, title, onClose, children }: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      aria-label={title}
      className={[
        'bg-surface-raised text-ink rounded-(--radius-card) border-border border-2 p-6',
        'm-auto w-[min(32rem,calc(100vw-2rem))] backdrop:bg-black/50',
      ].join(' ')}
    >
      <h2 className="text-(length:--text-title) mb-3">{title}</h2>
      {children}
    </dialog>
  )
}
